/**
 * Referral Tracking Utility
 *
 * Captures affiliate referral codes from URL parameters and stores them
 * for later use during user signup.
 *
 * Security: Referral codes are sanitized using an allowlist approach.
 * Storage: Uses localStorageWithTTL for consistent expiration handling.
 */

import { functions } from '@/firebase';
import { httpsCallable } from 'firebase/functions';
import { logger } from '@/utils/logger';
import { setItemWithTTL, getItemWithTTL, removeItemWithTTL, TTL } from '@/utils/localStorageWithTTL';

// ============================================================================
// Types
// ============================================================================

/**
 * Response from the trackReferral Cloud Function
 */
export interface TrackReferralResponse {
  success: boolean;
  message?: string;
  referralId?: string;
  affiliateId?: string;
}

/**
 * Parameters for tracking a referral
 */
interface TrackReferralParams {
  userId: string;
  referralCode: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

// ============================================================================
// Constants
// ============================================================================

const REFERRAL_CODE_KEY = 'affiliate_ref_code';
const MIN_CODE_LENGTH = 4;
const MAX_CODE_LENGTH = 50;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000; // 1 second base delay

// ============================================================================
// Helpers
// ============================================================================

/**
 * Sanitize referral code using allowlist approach
 * Only allows alphanumeric characters, dash, and underscore
 * This is more secure than blocklist - we only allow what we want
 */
function sanitizeReferralCode(code: string): string {
  return code
    // Only allow alphanumeric, dash, underscore (allowlist approach)
    .replace(/[^A-Z0-9_-]/gi, '')
    // Normalize to uppercase
    .toUpperCase()
    // Trim whitespace (already removed by regex, but explicit)
    .trim()
    // Limit length
    .substring(0, MAX_CODE_LENGTH);
}

/**
 * Sleep helper for retry logic
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Capture referral code from URL if present
 * Call this on app initialization (e.g., in App.tsx or a root component)
 *
 * Security: Code is sanitized using allowlist before storage.
 * Storage: Uses a 90-day TTL via localStorageWithTTL (affiliate attribution window).
 */
export function captureReferralCode(): void {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');

    // No ref parameter is expected/normal - not an error
    if (!refCode || !refCode.trim()) {
      return;
    }

    const sanitizedCode = sanitizeReferralCode(refCode);

    // Validate minimum length after sanitization
    if (sanitizedCode.length < MIN_CODE_LENGTH) {
      logger.warn('ReferralTracking', 'Referral code too short after sanitization', {
        originalLength: refCode.length,
        sanitizedLength: sanitizedCode.length
      });
      return;
    }

    setItemWithTTL(REFERRAL_CODE_KEY, sanitizedCode, TTL.THREE_MONTHS);

    logger.info('ReferralTracking', 'Captured referral code', {
      code: sanitizedCode.substring(0, 4) + '...',
      length: sanitizedCode.length
    });
  } catch (error) {
    // Actual error (localStorage failure, etc.)
    logger.error('ReferralTracking', 'Error capturing referral code', { error });
  }
}

/**
 * Get the stored referral code if it exists and hasn't expired
 * Returns null if no code exists or if expired (both expected conditions).
 */
export function getStoredReferralCode(): string | null {
  try {
    return getItemWithTTL<string>(REFERRAL_CODE_KEY);
  } catch (error) {
    // Actual error (localStorage corruption, etc.)
    logger.error('ReferralTracking', 'Error reading referral code from storage', { error });
    return null;
  }
}

/**
 * Clear the stored referral code
 */
export function clearReferralCode(): void {
  try {
    removeItemWithTTL(REFERRAL_CODE_KEY);
  } catch (error) {
    // Log but don't throw - cleanup failure is non-critical
    logger.warn('ReferralTracking', 'Error clearing referral code', { error });
  }
}

/**
 * Track the referral with the backend
 * Call this after a user successfully creates an account
 *
 * Includes retry logic to handle race conditions where the user document
 * might not exist yet in Firestore when the backend tries to link the referral.
 *
 * @param userId The Firebase user ID of the newly registered user
 * @returns true if tracking was successful, false otherwise
 */
export async function trackReferralForUser(userId: string): Promise<boolean> {
  const referralCode = getStoredReferralCode();

  // No referral code is expected/normal - not an error
  if (!referralCode) {
    logger.debug('ReferralTracking', 'No referral code to track');
    return false;
  }

  // Get UTM parameters from current URL (captured at time of tracking)
  const urlParams = new URLSearchParams(window.location.search);
  const params: TrackReferralParams = {
    userId,
    referralCode,
    utmSource: urlParams.get('utm_source') || undefined,
    utmMedium: urlParams.get('utm_medium') || undefined,
    utmCampaign: urlParams.get('utm_campaign') || undefined
  };

  // Retry logic to handle race conditions
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
    try {
      const trackReferral = httpsCallable<TrackReferralParams, TrackReferralResponse>(
        functions,
        'trackReferral'
      );

      const result = await trackReferral(params);
      const data = result.data;

      if (data.success) {
        logger.info('ReferralTracking', 'Successfully tracked referral', {
          userId,
          code: referralCode.substring(0, 4) + '...',
          attempt
        });
        // Clear the code after successful tracking
        clearReferralCode();
        return true;
      } else {
        // Backend returned success: false - don't retry
        logger.warn('ReferralTracking', 'Referral tracking returned false', {
          message: data.message,
          attempt
        });
        return false;
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Check if this is a retryable error
      const isRetryable = isRetryableError(lastError);

      if (isRetryable && attempt < MAX_RETRY_ATTEMPTS) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = RETRY_DELAY_MS * Math.pow(2, attempt - 1);
        logger.warn('ReferralTracking', 'Retrying referral tracking', {
          attempt,
          maxAttempts: MAX_RETRY_ATTEMPTS,
          delayMs: delay,
          error: lastError.message
        });
        await sleep(delay);
      } else {
        // Non-retryable or max attempts reached
        break;
      }
    }
  }

  // All retries exhausted
  logger.error('ReferralTracking', 'Failed to track referral after retries', {
    userId,
    code: referralCode.substring(0, 4) + '...',
    attempts: MAX_RETRY_ATTEMPTS,
    error: lastError?.message
  });

  // Don't clear the code on error - user might retry or it could be tracked on next login
  return false;
}

/**
 * Determine if an error is retryable
 * Retryable: network errors, temporary unavailability, rate limiting
 * Non-retryable: validation errors, auth errors, not found
 */
function isRetryableError(error: Error): boolean {
  const message = error.message.toLowerCase();

  // Non-retryable error patterns
  const nonRetryablePatterns = [
    'invalid',
    'not found',
    'unauthorized',
    'permission',
    'already exists',
    'already tracked'
  ];

  if (nonRetryablePatterns.some(pattern => message.includes(pattern))) {
    return false;
  }

  // Retryable error patterns
  const retryablePatterns = [
    'network',
    'timeout',
    'unavailable',
    'internal',
    'rate limit',
    'too many requests',
    'failed to fetch'
  ];

  return retryablePatterns.some(pattern => message.includes(pattern));
}
